import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Download, Loader2, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentReceiptView() {
  const navigate = useNavigate();
  const { receiptNo } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [receiptData, setReceiptData] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchReceipt();
  }, [navigate, receiptNo]);

  const fetchReceipt = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/receipt/${receiptNo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setReceiptData(await response.json());
      } else {
        toast.error('Receipt not found');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error fetching receipt:', error);
      toast.error('Failed to load receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const windowPrint = window.open('', '', 'width=800,height=600');
    
    windowPrint.document.write(`
      <html>
        <head>
          <title>Receipt - ${receiptNo}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .receipt-container { max-width: 600px; margin: 0 auto; border: 2px solid #333; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 15px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header p { margin: 5px 0; color: #666; }
            .receipt-title { text-align: center; background: #4F46E5; color: white; padding: 10px; margin: 15px 0; font-size: 18px; font-weight: bold; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
            .info-item { padding: 8px; background: #f5f5f5; }
            .info-item label { font-size: 12px; color: #666; display: block; }
            .info-item span { font-weight: bold; }
            .amount-section { text-align: center; padding: 20px; background: #e8f5e9; margin: 15px 0; }
            .amount-section .amount { font-size: 32px; font-weight: bold; color: #2e7d32; }
            .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px dashed #ccc; }
            .footer p { margin: 5px 0; font-size: 12px; color: #666; }
            .status { display: inline-block; padding: 5px 15px; background: #4caf50; color: white; border-radius: 20px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    windowPrint.document.close();
    windowPrint.focus();
    windowPrint.print();
    windowPrint.close();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!receiptData) {
    return null;
  }

  const { receipt, student, institution } = receiptData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-xl font-semibold">Payment Receipt</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardContent className="p-6">
            {/* Printable Receipt Content */}
            <div ref={printRef} className="receipt-container">
              {/* Institution Header */}
              <div className="header text-center border-b-2 border-gray-800 pb-4 mb-4">
                {institution?.logo_url && (
                  <img src={institution.logo_url} alt="Logo" className="h-16 mx-auto mb-2" />
                )}
                <h1 className="text-2xl font-bold text-gray-800">{institution?.name || 'Institution'}</h1>
                {institution?.address && (
                  <p className="text-sm text-gray-600">{institution.address}</p>
                )}
                {institution?.phone && (
                  <p className="text-sm text-gray-600">Phone: {institution.phone}</p>
                )}
              </div>

              {/* Receipt Title */}
              <div className="receipt-title bg-indigo-600 text-white text-center py-3 font-bold text-lg rounded">
                PAYMENT RECEIPT
              </div>

              {/* Receipt Info */}
              <div className="grid grid-cols-2 gap-4 my-4">
                <div className="p-3 bg-gray-50 rounded">
                  <label className="text-xs text-gray-500 block">Receipt No.</label>
                  <span className="font-bold text-indigo-600">{receipt?.receipt_no}</span>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <label className="text-xs text-gray-500 block">Date</label>
                  <span className="font-bold">{receipt?.payment_date}</span>
                </div>
              </div>

              {/* Student Info */}
              <div className="border rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-700 mb-3 border-b pb-2">Student Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Student Name</label>
                    <p className="font-medium">{student?.full_name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Student ID</label>
                    <p className="font-medium">{student?.student_id}</p>
                  </div>
                  {student?.course_name && (
                    <div>
                      <label className="text-xs text-gray-500">Course</label>
                      <p className="font-medium">{student.course_name}</p>
                    </div>
                  )}
                  {student?.session && (
                    <div>
                      <label className="text-xs text-gray-500">Session</label>
                      <p className="font-medium">{student.session}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="border rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-700 mb-3 border-b pb-2">Payment Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Fee Type</label>
                    <p className="font-medium">{receipt?.fee_type || receipt?.description || 'Fee Payment'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Payment Mode</label>
                    <p className="font-medium">{receipt?.payment_mode}</p>
                  </div>
                  {receipt?.transaction_id && (
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500">Transaction ID</label>
                      <p className="font-medium text-sm break-all">{receipt.transaction_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount Section */}
              <div className="amount-section bg-green-50 text-center py-6 rounded-lg border-2 border-green-200">
                <p className="text-sm text-gray-600 mb-1">Amount Paid</p>
                <p className="amount text-4xl font-bold text-green-600">
                  ₹{receipt?.amount?.toLocaleString('en-IN')}
                </p>
                <div className="mt-3">
                  <span className="status inline-flex items-center gap-1 bg-green-500 text-white px-4 py-1 rounded-full text-sm">
                    <CheckCircle className="w-4 h-4" /> PAID
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="footer text-center mt-6 pt-4 border-t border-dashed border-gray-300">
                <p className="text-xs text-gray-500">This is a computer generated receipt.</p>
                <p className="text-xs text-gray-500">Thank you for your payment!</p>
                <p className="text-xs text-gray-400 mt-2">
                  Generated on: {new Date().toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
